import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client, { fetchFileBlob, API, getToken } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UploadDialog } from '@/components/UploadDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Eye, Plus, FileText, MoreVertical, Download, Archive, RefreshCw, Search, FileStack, AlertTriangle, FileWarning, Check, X, Trash2 } from 'lucide-react';

const fmtSize = (b) => !b ? '-' : b > 1048576 ? `${(b/1048576).toFixed(2)} MB` : `${(b/1024).toFixed(0)} KB`;

const expiryInfo = (exp) => {
  if (!exp) return null;
  const d = new Date(exp);
  if (isNaN(d.getTime())) return null;
  const days = Math.ceil((d - new Date()) / 86400000);
  if (days < 0) return { label: 'Expired', cls: 'bg-[hsl(var(--danger))]/12 text-[hsl(var(--danger))]' };
  if (days <= 30) return { label: days === 0 ? 'Expires today' : `Expires in ${days}d`, cls: 'bg-[hsl(var(--warning))]/18 text-[hsl(var(--warning-foreground))]' };
  return { label: `Valid · ${d.toLocaleDateString()}`, cls: 'bg-[hsl(var(--success))]/12 text-[hsl(var(--success))]' };
};

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('expiry_asc');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const canReview = ['developer', 'company_admin', 'der', 'owner_operator'].includes(user?.role);
  const canDelete = ['developer', 'company_admin', 'owner_operator'].includes(user?.role);

  const load = () => {
    setDocs(null);
    const url = tab === 'pending' ? '/documents?status=pending' : tab === 'archived' ? '/documents?include_archived=true' : '/documents';
    client.get(url).then((r) => setDocs(tab === 'archived' ? r.data.filter((d) => d.archived) : r.data)).catch(() => setDocs([]));
    client.get('/documents/metrics').then((r) => setMetrics(r.data)).catch(() => {});
  };
  useEffect(load, [tab]);

  const review = async (id, action) => {
    try { await client.post(`/documents/${id}/review`, { action }); toast.success(`Document ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const archive = async (id) => {
    try { await client.post(`/documents/${id}/archive`); toast.success('Document archived'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doDelete = async () => {
    if (!toDelete) return;
    try { await client.delete(`/documents/${toDelete.id}`); toast.success('Document deleted'); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const openPreview = async (doc) => {
    try { const url = await fetchFileBlob(doc.id); setPreview({ url, doc }); }
    catch { toast.error('Preview unavailable (seed doc has no file)'); }
  };
  const downloadDoc = async (doc) => {
    try {
      const blob = await fetchFileBlob(doc.id);
      const a = document.createElement('a'); a.href = blob; a.download = doc.file_name; a.click();
      setTimeout(() => URL.revokeObjectURL(blob), 1000);
    } catch { toast.error('Download unavailable for seed doc'); }
  };

  const filtered = (docs || []).filter((d) => {
    const s = q.toLowerCase();
    return !s || d.file_name?.toLowerCase().includes(s) || d.driver_name?.toLowerCase().includes(s) || d.power_unit_label?.toLowerCase().includes(s) || (d.tags||[]).join(' ').toLowerCase().includes(s);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'recent') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    const da = a.expires_at ? new Date(a.expires_at).getTime() : null;
    const db = b.expires_at ? new Date(b.expires_at).getTime() : null;
    if (da === null && db === null) return 0;
    if (da === null) return 1;   // documents with no expiry sink to the bottom
    if (db === null) return -1;
    return sort === 'expiry_desc' ? db - da : da - db; // soonest/most-overdue first by default
  });

  const METRICS = [
    { label: 'Total Documents', v: metrics?.total_count, icon: FileStack, color: 'var(--primary)' },
    { label: 'Expiring Soon', v: metrics?.expiring_count, icon: AlertTriangle, color: 'var(--warning)', hint: 'Within 14 days' },
    { label: 'Missing DQ Files', v: metrics?.missing_dq_count, icon: FileWarning, color: 'var(--danger)', hint: 'Incomplete DQ files' },
  ];

  return (
    <AppShell title="Documents">
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        {METRICS.map((m) => { const Ic = m.icon; return (
          <Card key={m.label} className="flex items-center gap-3 p-4" data-testid={`doc-metric-${m.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `hsl(${m.color} / 0.12)` }}><Ic className="h-5 w-5" style={{ color: `hsl(${m.color})` }} /></div>
            <div><div className="text-xs uppercase tracking-wide text-muted-foreground">{m.label}</div><div className="font-display text-2xl font-bold tabular-nums">{m.v ?? '—'}</div>{m.hint && <div className="text-[10px] text-muted-foreground">{m.hint}</div>}</div>
          </Card>
        ); })}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}><TabsList>
          <TabsTrigger value="all" data-testid="documents-all-tab">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="documents-pending-tab">Pending Review</TabsTrigger>
          <TabsTrigger value="archived" data-testid="documents-archived-tab">Archived</TabsTrigger>
        </TabsList></Tabs>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5"><Search className="h-4 w-4 text-muted-foreground" /><input placeholder="Search name, driver, or power unit…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-transparent text-sm outline-none" data-testid="documents-search-input" /></div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px]" data-testid="documents-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="expiry_asc" data-testid="documents-sort-expiry-asc">Soonest expiry</SelectItem>
              <SelectItem value="expiry_desc">Latest expiry</SelectItem>
              <SelectItem value="recent">Recently uploaded</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setUploadOpen(true)} data-testid="documents-upload-button"><Plus className="mr-1.5 h-4 w-4" />Upload</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {docs === null ? <div className="space-y-2 p-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-12" />)}</div> : sorted.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No documents found.</div>
        ) : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Document Name</TableHead><TableHead>Category Tags</TableHead><TableHead>Uploaded</TableHead><TableHead>Driver</TableHead><TableHead>Power Unit</TableHead><TableHead>Size</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {sorted.map((doc) => (
                <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                  <TableCell className="font-medium text-[hsl(var(--primary))]">
                    <button onClick={() => openPreview(doc)} className="flex items-center gap-1.5 text-left hover:underline"><FileText className="h-4 w-4 shrink-0" /><span className="line-clamp-1 max-w-[220px]">{doc.file_name}</span></button>
                    {(() => { const ex = expiryInfo(doc.expires_at); return ex ? <span className={`mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${ex.cls}`} data-testid={`doc-expiry-badge-${doc.id}`}>{ex.label.startsWith('Valid') ? null : <AlertTriangle className="h-3 w-3" />}{ex.label}</span> : null; })()}
                  </TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{(doc.tags || []).slice(0, 4).map((t) => <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">{t}</span>)}</div></TableCell>
                  <TableCell className="text-sm">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="text-sm">{doc.driver_name || '-'}</TableCell>
                  <TableCell className="text-sm">{doc.power_unit_label || '-'}</TableCell>
                  <TableCell className="text-sm tabular-nums">{fmtSize(doc.size)}</TableCell>
                  <TableCell>{doc.archived ? <StatusBadge status="terminated" label="Archived" /> : <StatusBadge status={doc.status} />}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canReview && doc.status === 'pending' && !doc.archived && (<>
                        <Button variant="ghost" size="icon" className="text-[hsl(var(--success))]" onClick={() => review(doc.id, 'approve')} data-testid={`document-approve-${doc.id}`}><Check className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-[hsl(var(--danger))]" onClick={() => review(doc.id, 'reject')} data-testid={`document-reject-${doc.id}`}><X className="h-4 w-4" /></Button>
                      </>)}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`document-actions-${doc.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPreview(doc)}><Eye className="mr-2 h-4 w-4" />View</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadDoc(doc)}><Download className="mr-2 h-4 w-4" />Download</DropdownMenuItem>
                          {canReview && !doc.archived && <DropdownMenuItem onClick={() => archive(doc.id)} data-testid={`document-archive-${doc.id}`}><Archive className="mr-2 h-4 w-4" />Archive</DropdownMenuItem>}
                          {canReview && <DropdownMenuItem onClick={() => setUploadOpen(true)}><RefreshCw className="mr-2 h-4 w-4" />Replace (upload new)</DropdownMenuItem>}
                          {canDelete && <DropdownMenuItem onClick={() => setToDelete(doc)} className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" data-testid={`document-delete-${doc.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        )}
      </Card>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="document-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.file_name}" will be permanently removed from the active records. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="document-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="document-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>{preview?.doc?.file_name}</DialogTitle></DialogHeader>
          {preview && (preview.doc.content_type?.includes('image') ? <img src={preview.url} alt="doc" className="max-h-[70vh] w-full object-contain" /> : <iframe title="d" src={preview.url} className="h-[70vh] w-full rounded-lg border" />)}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
